/**
 * Splits a Server-Sent Events byte stream into complete event payloads.
 *
 * SSE frames are separated by a blank line; within a frame, `data:` lines carry
 * the payload and lines starting with `:` are comments (the backend sends
 * `: connected` once and `: keep-alive` every 20s). `push` returns the JSON
 * payload string of every frame that completed in the chunk, buffering any
 * partial trailing frame until the next chunk arrives.
 */
export class SseFrameBuffer {
  private buffer = '';

  push(chunk: string): string[] {
    this.buffer += chunk;
    const payloads: string[] = [];

    let separator = this.nextSeparator();

    while (separator !== -1) {
      const frame = this.buffer.slice(0, separator.index);
      this.buffer = this.buffer.slice(separator.index + separator.length);

      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice('data:'.length).trimStart())
        .join('\n');

      if (data.length > 0) payloads.push(data);

      separator = this.nextSeparator();
    }

    return payloads;
  }

  // SSE blank-line delimiter, tolerating both `\n\n` and `\r\n\r\n`.
  private nextSeparator(): { index: number; length: number } | -1 {
    const lf = this.buffer.indexOf('\n\n');
    const crlf = this.buffer.indexOf('\r\n\r\n');

    if (lf === -1 && crlf === -1) return -1;

    if (crlf === -1 || (lf !== -1 && lf < crlf)) return { index: lf, length: 2 };

    return { index: crlf, length: 4 };
  }
}
